import React, {useEffect} from 'react';
import {fontObserver} from '../../helper/font';
import sanity, {imageUrl} from '../../lib/sanity';
import Page from '../../components/page';
import Header from '../../components/header';
import {ContentSection} from '../../components/section';
import Content from '../../components/content';
import Footer from '../../components/footer';
import DownloadList from '../../components/downloadList';
import Tabs from '../../components/tabs';
import SeoHeader, {ProductSeoHeader} from '../../components/seoHeader';
import * as gtag from '../../lib/gtag';

const productQuery = (slug) => `*[_type == "product" && slug.current=="${slug}"] {
  _id,
  title,
  slug,
  image,
  description,
  content,
  "attachments": attachments[]{
    title,
    "file": file.asset->url,
  },
  "tabs": tabs[]{
    title,
    content,
    "images": images[]{
      asset,
      "height": asset->metadata.dimensions.height,
      "width": asset->metadata.dimensions.width,
    } 
  }
}[0]`;

const allProductQuery = `*[_type == "product"] {
  slug
}`;

const Product = ({product}) => {
  useEffect(() => {
    fontObserver();
  }, []);
  return (
    <Page>
      <SeoHeader
        title={product?.title}
        description={product?.description}
        image={imageUrl(product?.image, 80, 600)}
        url={`/produkt/${product?.slug?.current}`}
      />
      <ProductSeoHeader
        name={product?.title}
        brand={'Pernikl'}
        description={product?.description}
        images={[imageUrl(product?.image, 80, 1000)]}
      />
      <Header
        title={product?.title}
        description={product?.description}
        placeholderImage={imageUrl(product?.image, 10, 10)}
        largeImage={imageUrl(product?.image, 80, 1000)}
      />
      <ContentSection>
        <Content content={product?.content} />
      </ContentSection>
      {product?.attachments?.length > 0 && (
        <ContentSection>
          <DownloadList
            items={product?.attachments?.map((item) => ({
              title: item?.title,
              link: item?.file,
            }))}
            onClick={({title, link}) =>
              gtag.event({
                category: 'product',
                action: 'download',
                label: `download '${title}' for product '${product?.title}'`,
                value: link,
              })
            }
          />
        </ContentSection>
      )}
      {product?.tabs?.length > 0 && (
        <ContentSection>
          <Tabs
            items={product?.tabs?.map((item) => ({
              title: item?.title,
              content: item?.content,
              images: item?.images,
            }))}
            onClick={(tab) =>
              gtag.event({
                category: 'product',
                action: 'tab',
                label: `change tab for product '${product?.title}'`,
                value: tab,
              })
            }
          />
        </ContentSection>
      )}
      <Footer />
    </Page>
  );
};

export const getStaticProps = async (context) => {
  const product = await sanity.fetch(productQuery(context.params?.slug?.toString()));
  if (!product) {
    return {props: {}, revalidate: 1, notFound: true};
  }
  return {
    props: {product},
    revalidate: 10,
  };
};

export const getStaticPaths = async () => {
  const products = await sanity.fetch(allProductQuery);
  return {
    paths: products?.map((item) => ({params: {slug: item?.slug?.current}})) || [],
    fallback: true,
  };
};

export default Product;
